import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronDown, HelpCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import {
  listAdminTickets,
  updateAdminTicketStatus,
  type SupportTicketSummary,
} from '../../services/helpTicketService';
import { useAdminHelpUnread } from '../../components/self-service/help/useAdminHelpUnread';
import TicketThreadPanel from '../../components/self-service/help/TicketThreadPanel';
import TicketCampaignDetailsPanel from '../../components/self-service/help/TicketCampaignDetailsPanel';
import {
  ADMIN_TICKET_STATUS_LABELS,
  ADMIN_TICKET_STATUS_ORDER,
  countUnreadTicketsInStation,
  groupTicketsByStationAndStatus,
} from '../../components/self-service/help/adminTicketGrouping';
import { formatDateTime } from '../../components/self-service/shared/dateFormat';
import { ApiError } from '../../lib/apiClient';
import {
  ticketListCampaignName,
  ticketListIssueName,
} from '../../components/self-service/help/ticketContactInfo';

function statusPill(status: SupportTicketSummary['status']) {
  switch (status) {
    case 'open':
      return 'bg-yellow-100 text-yellow-800';
    case 'in_progress':
      return 'bg-blue-100 text-blue-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
  }
}

const STATUS_ORDER = ADMIN_TICKET_STATUS_ORDER;
const STATUS_SECTION_LABELS = ADMIN_TICKET_STATUS_LABELS;

interface TicketListButtonProps {
  ticket: SupportTicketSummary;
  selected: boolean;
  onSelect: (id: string) => void;
}

const TicketListButton: React.FC<TicketListButtonProps> = ({ ticket, selected, onSelect }) => {
  const campaignName = ticketListCampaignName(ticket.details);
  const issueName = ticketListIssueName(ticket);

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(ticket.id)}
        className={`w-full text-left rounded-lg px-3 py-2.5 text-sm transition-colors ${
          selected ? 'bg-slate-100 ring-1 ring-slate-200' : 'hover:bg-slate-50'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 truncate">
              {campaignName ?? '—'}
            </div>
            <div className="text-xs text-slate-600 truncate mt-0.5">{issueName}</div>
            <div className="text-[10px] text-slate-400 mt-1">{formatDateTime(ticket.createdAt)}</div>
          </div>
          {ticket.unreadForAdmin && (
            <span className="shrink-0 h-2 w-2 rounded-full bg-red-500 mt-1.5" />
          )}
        </div>
      </button>
    </li>
  );
};

const AdminHelpCenterPage: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const isSuperAdmin = user?.rawRole === 'super_admin';
  const { refresh: refreshUnread } = useAdminHelpUnread(isSuperAdmin);

  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listAdminTickets();
      setTickets(rows);
      setSelectedId((prev) => (prev && rows.some((r) => r.id === prev) ? prev : null));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) void load();
  }, [isSuperAdmin, load]);

  const grouped = useMemo(() => groupTicketsByStationAndStatus(tickets), [tickets]);

  const selected = tickets.find((t) => t.id === selectedId) ?? null;

  const handleSelectTicket = useCallback((ticketId: string) => {
    setSelectedId(ticketId);
    setTickets((prev) =>
      prev.map((t) => (t.id === ticketId ? { ...t, unreadForAdmin: false } : t)),
    );
  }, []);

  const handleTicketViewed = useCallback(
    (ticketId: string) => {
      setTickets((prev) =>
        prev.map((t) => (t.id === ticketId ? { ...t, unreadForAdmin: false } : t)),
      );
      void refreshUnread();
    },
    [refreshUnread],
  );

  const handleStatusChange = async (status: SupportTicketSummary['status']) => {
    if (!selectedId) return;
    setUpdatingStatus(true);
    try {
      await updateAdminTicketStatus(selectedId, status);
      await load();
      await refreshUnread();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to update status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <Navigate to="/admin" state={{ from: location.pathname }} replace />
    );
  }

  if (user.rawRole !== 'super_admin') {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-white px-6 py-4 flex items-center gap-4">
        <Link to="/admin" className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <HelpCircle className="w-5 h-5" />
        <h1 className="text-lg font-semibold">Help Center</h1>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <p className="text-sm text-slate-600 mb-6">
          Support tickets grouped by station and status.
        </p>

        {loading ? (
          <div className="text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <p className="text-red-600 text-sm">{error}</p>
        ) : tickets.length === 0 ? (
          <p className="text-slate-600 text-sm">No tickets yet.</p>
        ) : (
          <div className="grid lg:grid-cols-[minmax(0,340px)_1fr] gap-6">
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              {grouped.map((station) => {
                const stationTicketCount =
                  station.byStatus.open.length +
                  station.byStatus.in_progress.length +
                  station.byStatus.completed.length;
                const stationUnreadCount = countUnreadTicketsInStation(station);

                return (
                <details
                  key={station.stationSlug}
                  className="group/station border border-slate-200 rounded-xl bg-white overflow-hidden"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-sm font-semibold text-slate-900 px-3 py-2.5 border-b border-slate-100 bg-slate-50 hover:bg-slate-100 [&::-webkit-details-marker]:hidden">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="truncate">{station.stationName}</span>
                      <ChevronDown
                        className="w-4 h-4 shrink-0 text-slate-400 transition-transform group-open/station:rotate-180"
                        aria-hidden
                      />
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-normal text-slate-400 shrink-0">
                      {stationUnreadCount > 0 && (
                        <span
                          className="h-2 w-2 rounded-full bg-red-500 shrink-0"
                          aria-label={`${stationUnreadCount} unread ticket${stationUnreadCount === 1 ? '' : 's'}`}
                        />
                      )}
                      ({stationTicketCount})
                    </span>
                  </summary>
                  <div className="divide-y divide-slate-100">
                    {STATUS_ORDER.map((status) => {
                      const items = station.byStatus[status];
                      if (items.length === 0) return null;

                      return (
                        <details key={status} open className="group/status">
                          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-50 [&::-webkit-details-marker]:hidden">
                            <span className="flex items-center gap-1.5">
                              <span
                                className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full normal-case tracking-normal ${statusPill(status)}`}
                              >
                                {STATUS_SECTION_LABELS[status]}
                              </span>
                              <ChevronDown
                                className="w-4 h-4 shrink-0 text-slate-400 transition-transform group-open/status:rotate-180"
                                aria-hidden
                              />
                            </span>
                            <span className="text-slate-400 font-normal normal-case tracking-normal shrink-0">
                              ({items.length})
                            </span>
                          </summary>
                          <ul className="space-y-1 px-2 pb-2">
                            {items.map((t) => (
                              <TicketListButton
                                key={t.id}
                                ticket={t}
                                selected={selectedId === t.id}
                                onSelect={handleSelectTicket}
                              />
                            ))}
                          </ul>
                        </details>
                      );
                    })}
                  </div>
                </details>
                );
              })}
            </div>

            <div className="border border-slate-200 rounded-xl bg-white p-4 min-h-[360px]">
              {selected ? (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4 pb-4 border-b border-slate-100">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">
                        {ticketListCampaignName(selected.details) ?? '—'}
                      </div>
                      <div className="text-sm text-slate-600 truncate mt-0.5">
                        {ticketListIssueName(selected)}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {formatDateTime(selected.createdAt)}
                      </div>
                    </div>
                    <label className="text-sm flex items-center gap-2">
                      <span className="text-slate-600">Status</span>
                      <select
                        value={selected.status}
                        disabled={updatingStatus}
                        onChange={(e) =>
                          void handleStatusChange(e.target.value as SupportTicketSummary['status'])
                        }
                        className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </label>
                  </div>
                  <details className="mb-4 group/campaign">
                    <summary className="cursor-pointer list-none text-sm font-semibold text-slate-800 hover:text-slate-900 [&::-webkit-details-marker]:hidden">
                      <span className="inline-flex items-center gap-1.5">
                        Campaign Details
                        <ChevronDown
                          className="w-4 h-4 text-slate-400 transition-transform group-open/campaign:rotate-180"
                          aria-hidden
                        />
                      </span>
                    </summary>
                    <div className="mt-2">
                      <TicketCampaignDetailsPanel details={selected.details} />
                    </div>
                  </details>
                  <TicketThreadPanel
                    key={selected.id}
                    ticketId={selected.id}
                    admin
                    onTicketViewed={handleTicketViewed}
                  />
                </>
              ) : (
                <p className="text-sm text-slate-500">No ticket selected</p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminHelpCenterPage;
